// P-075 · El selector global de periodos: la lógica, sin React.
//
// Dirección aprobada por el dueño (P-075):
//   Atajos: Hoy · Ayer · Antier · Últimos 7 días · Este mes · Otro periodo.
//   «Otro periodo» abre UN selector: Día | Semana | Mes | Personalizado.
//     Día:    Hoy · Ayer · Antier
//     Semana: Esta semana (lunes → hoy) · Semana anterior (lunes → domingo) · Últimas 3 semanas completas
//     Mes:    Este mes (día 1 → hoy) · Mes anterior (mes completo) · Últimos 3 meses completos
//     Personalizado: un solo calendario de rango (primer toque inicio, segundo toque fin).
//
// Las fechas son días de calendario 'YYYY-MM-DD', sin hora ni zona: lo que se le pide al servidor sigue
// siendo `dateFrom` / `dateTo` como antes. «Hoy» lo decide quien usa el selector (cada pantalla conserva el
// suyo: el día del equipo en las listas, como el calendario de antes; el de la tienda en Reportes). La
// aritmética se hace en UTC sobre la fecha, así que el horario de verano no mueve ningún día.

export interface DateRange {
  /** 'YYYY-MM-DD'; vacío = sin límite. */
  from: string;
  /** 'YYYY-MM-DD'; vacío = sin límite. */
  to: string;
}

export type PeriodId =
  | 'hoy' | 'ayer' | 'antier'
  | 'ultimos-7'
  | 'esta-semana' | 'semana-anterior' | 'ultimas-3-semanas'
  | 'este-mes' | 'mes-anterior' | 'ultimos-3-meses';

export type PeriodGroupId = 'dia' | 'semana' | 'mes' | 'personalizado';

export const PERIOD_LABELS: Record<PeriodId, string> = {
  hoy: 'Hoy',
  ayer: 'Ayer',
  antier: 'Antier',
  'ultimos-7': 'Últimos 7 días',
  'esta-semana': 'Esta semana',
  'semana-anterior': 'Semana anterior',
  'ultimas-3-semanas': 'Últimas 3 semanas completas',
  'este-mes': 'Este mes',
  'mes-anterior': 'Mes anterior',
  'ultimos-3-meses': 'Últimos 3 meses completos',
};

/** Los atajos que se ven sin abrir nada, en el orden aprobado. */
export const QUICK_PERIODS: readonly PeriodId[] = ['hoy', 'ayer', 'antier', 'ultimos-7', 'este-mes'];

/** Los segmentos de «Otro periodo», en el orden aprobado. */
export const PERIOD_GROUPS: ReadonlyArray<{ id: PeriodGroupId; label: string; periods: readonly PeriodId[] }> = [
  { id: 'dia', label: 'Día', periods: ['hoy', 'ayer', 'antier'] },
  { id: 'semana', label: 'Semana', periods: ['esta-semana', 'semana-anterior', 'ultimas-3-semanas'] },
  { id: 'mes', label: 'Mes', periods: ['este-mes', 'mes-anterior', 'ultimos-3-meses'] },
  { id: 'personalizado', label: 'Personalizado', periods: [] },
];

/** Los del modo de un solo día (el resumen de un día): Hoy · Ayer · Antier, y un día del calendario. */
export const DAY_PERIODS: readonly PeriodId[] = ['hoy', 'ayer', 'antier'];

const ALL_PERIODS = Object.keys(PERIOD_LABELS) as PeriodId[];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1_000;

/** Medianoche UTC de una fecha 'YYYY-MM-DD' que existe (no un 31 de febrero), o `null`. */
export function utcOf(iso: string): number | null {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const time = Date.UTC(year, month - 1, day);
  const back = new Date(time);
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null;
  return time;
}

export const isIsoDate = (value: string): boolean => utcOf(value) !== null;

const isoOfUtc = (time: number): string => new Date(time).toISOString().slice(0, 10);

/** 'YYYY-MM-DD' más (o menos) N días de calendario. */
export function addDays(iso: string, days: number): string {
  return isoOfUtc(utcOf(iso)! + days * DAY_MS);
}

/** Días de calendario entre dos fechas, contando las dos. */
export function inclusiveDays(from: string, to: string): number {
  return Math.round((utcOf(to)! - utcOf(from)!) / DAY_MS) + 1;
}

/** El día del equipo, como lo tomaba el calendario de antes (`new Date()` local). */
export function deviceToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** 0 = lunes … 6 = domingo. */
export function weekdayIndex(iso: string): number {
  return (new Date(utcOf(iso)!).getUTCDay() + 6) % 7;
}

/** El lunes de la semana de una fecha. */
export function mondayOf(iso: string): string {
  return addDays(iso, -weekdayIndex(iso));
}

/** El día 1 del mes de una fecha, desplazado `monthsBack` meses hacia atrás. */
export function firstOfMonth(iso: string, monthsBack = 0): string {
  const [year, month] = iso.split('-').map(Number) as [number, number];
  const time = Date.UTC(year, month - 1 - monthsBack, 1);
  return isoOfUtc(time);
}

/** El último día del mes anterior al de una fecha. */
export function lastOfPreviousMonth(iso: string): string {
  return addDays(firstOfMonth(iso), -1);
}

/** El rango de un periodo, con `today` como hoy. */
export function periodRange(id: PeriodId, today: string): DateRange {
  switch (id) {
    case 'hoy': return { from: today, to: today };
    case 'ayer': { const day = addDays(today, -1); return { from: day, to: day }; }
    case 'antier': { const day = addDays(today, -2); return { from: day, to: day }; }
    case 'ultimos-7': return { from: addDays(today, -6), to: today };
    case 'esta-semana': return { from: mondayOf(today), to: today };
    case 'semana-anterior': { const monday = mondayOf(today); return { from: addDays(monday, -7), to: addDays(monday, -1) }; }
    case 'ultimas-3-semanas': { const monday = mondayOf(today); return { from: addDays(monday, -21), to: addDays(monday, -1) }; }
    case 'este-mes': return { from: firstOfMonth(today), to: today };
    case 'mes-anterior': return { from: firstOfMonth(today, 1), to: lastOfPreviousMonth(today) };
    case 'ultimos-3-meses': return { from: firstOfMonth(today, 3), to: lastOfPreviousMonth(today) };
    default: return { from: today, to: today };
  }
}

/**
 * El periodo con nombre que coincide con un rango, o `null`. Si dos coinciden (el día 1, «Este mes» y
 * «Hoy» son el mismo día; un lunes, «Esta semana» y «Hoy»), gana el primero de `candidates`, que por
 * omisión sigue el orden de los atajos: lo que la persona tocó casi siempre es el atajo.
 */
export function matchPeriod(range: DateRange, today: string, candidates: readonly PeriodId[] = [...QUICK_PERIODS, ...ALL_PERIODS]): PeriodId | null {
  if (!range.from || !range.to) return null;
  for (const id of candidates) {
    const candidate = periodRange(id, today);
    if (candidate.from === range.from && candidate.to === range.to) return id;
  }
  return null;
}

/** El grupo de «Otro periodo» en el que vive un periodo (para abrir el selector donde está lo elegido). */
export function groupOf(id: PeriodId | null): PeriodGroupId {
  if (!id) return 'personalizado';
  if (id === 'ultimos-7') return 'personalizado';
  return PERIOD_GROUPS.find((group) => group.periods.includes(id))?.id ?? 'personalizado';
}

// ─────────────────────────────────────────────────────────────────────────────
// En palabras
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MONTHS_LONG = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const WEEKDAYS_LONG = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

function parts(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  return { year, month, day };
}

/** «9 oct», «9 oct 2025» si no es el año de hoy. */
export function shortDay(iso: string, today: string): string {
  const { year, month, day } = parts(iso);
  const sameYear = year === parts(today).year;
  return `${day} ${MONTHS_SHORT[month - 1]}${sameYear ? '' : ` ${year}`}`;
}

/** «jueves 9 de octubre de 2026»: el nombre completo de un día, para quien no ve la rejilla. */
export function longDay(iso: string): string {
  const { year, month, day } = parts(iso);
  return `${WEEKDAYS_LONG[weekdayIndex(iso)]} ${day} de ${MONTHS_LONG[month - 1]} de ${year}`;
}

/** «octubre 2026». */
export function monthTitle(year: number, monthIndex: number): string {
  return `${MONTHS_LONG[monthIndex]} ${year}`;
}

/** «3 – 9 oct», «28 sep – 4 oct», «9 oct», «desde 3 oct», «hasta 9 oct», «Todas las fechas». */
export function formatRange(range: DateRange, today: string): string {
  const { from, to } = range;
  if (!from && !to) return 'Todas las fechas';
  if (from && !to) return `desde ${shortDay(from, today)}`;
  if (!from && to) return `hasta ${shortDay(to, today)}`;
  if (from === to) return shortDay(from, today);
  const a = parts(from);
  const b = parts(to);
  const todayYear = parts(today).year;
  if (a.year === b.year && a.month === b.month && a.year === todayYear) return `${a.day} – ${b.day} ${MONTHS_SHORT[b.month - 1]}`;
  if (a.year === b.year && a.year === todayYear) return `${a.day} ${MONTHS_SHORT[a.month - 1]} – ${b.day} ${MONTHS_SHORT[b.month - 1]}`;
  return `${shortDay(from, '0000-01-01')} – ${shortDay(to, '0000-01-01')}`;
}

/**
 * Lo que dice el botón del periodo: el nombre del periodo si coincide con uno («Este mes»), «Últimos N días»
 * si termina hoy y empieza N−1 días antes (los 30 días con los que abren Compras y Movimientos), o las fechas.
 */
export function describePeriod(range: DateRange, today: string): string {
  if (!range.from && !range.to) return 'Todas las fechas';
  const named = matchPeriod(range, today);
  if (named) return PERIOD_LABELS[named];
  if (range.from && range.to && range.to === today && isIsoDate(range.from) && range.from < today) {
    return `Últimos ${inclusiveDays(range.from, range.to)} días`;
  }
  return formatRange(range, today);
}

/** Lo que impide aplicar un rango del calendario, o `null`. */
export function rangeProblem(range: DateRange, { maxDays, maxDay }: { maxDays?: number; maxDay?: string } = {}): string | null {
  if (!isIsoDate(range.from) || !isIsoDate(range.to)) return 'Elige el primer y el último día.';
  if (range.from > range.to) return 'El último día tiene que ser el mismo que el primero o uno posterior.';
  if (maxDay && range.to > maxDay) return 'No se pueden elegir días que todavía no llegan.';
  if (maxDays && inclusiveDays(range.from, range.to) > maxDays) return `Elige un periodo de ${maxDays} días o menos.`;
  return null;
}

/**
 * La rejilla de un mes: semanas de lunes a domingo; `null` en las casillas que no son del mes (se dejan
 * vacías: así el foco con flechas no se pierde en días de otro mes).
 */
export function monthCells(year: number, monthIndex: number): Array<string | null> {
  const first = isoOfUtc(Date.UTC(year, monthIndex, 1));
  const days = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const leading = weekdayIndex(first);
  const cells: Array<string | null> = Array.from({ length: leading }, () => null);
  for (let day = 1; day <= days; day += 1) cells.push(addDays(first, day - 1));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
